import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ClearanceCategoryNode, getClearanceCategoryTree } from "@SupplyLand/Services/SupplyLandService";
import { applyClearanceProductIds } from "@SupplyLand/Store/Pages/ProductList/ClearanceProductFilter";
import {
    branchHasSelection,
    buildMaps,
    collectSelfAndDescendants,
    computeProductIds,
    deriveSelectedCategoryIds,
    isClearanceCategory,
    sumBranchProductCount,
} from "@SupplyLand/Utilities/clearanceCategoryTree";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import debounce from "lodash/debounce";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";

const enum fields {
    expandByDefault = "expandByDefault",
}

interface OwnProps extends WidgetProps, HasCategoryContext {
    fields: {
        [fields.expandByDefault]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    appliedProductIds: state.pages.productList.parameter?.productIds as string[] | undefined,
    isCategoryPage: state.pages.productList.productFilters.pageCategoryId !== undefined,
});

const mapDispatchToProps = {
    applyClearanceProductIds,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

interface CategoryCheckboxTreeProps {
    nodes: ClearanceCategoryNode[];
    selectedCategoryIds: Set<string>;
    depth: number;
    onToggle: (node: ClearanceCategoryNode) => void;
}

const CategoryCheckboxTree: FC<CategoryCheckboxTreeProps> = ({ nodes, selectedCategoryIds, depth, onToggle }) => (
    <>
        {nodes.map(node => {
            const isSelected = selectedCategoryIds.has(node.id);
            const children = node.subCategories ?? [];
            // Parent categories roll up the product counts of their whole branch; leaves keep their own.
            const displayCount = children.length > 0 ? sumBranchProductCount(node) : node.productCount;
            return (
                <React.Fragment key={node.id}>
                    <CheckboxGroup
                        css={css`
                            width: 100%;
                            padding-left: 16px;
                            &:not(:last-child) {
                                padding-bottom: 5px;
                            }
                        `}
                    >
                        <Checkbox
                            checked={isSelected}
                            onChange={() => onToggle(node)}
                            data-test-selector={`clearanceCategoryFacet${node.id}`}
                            typographyProps={isSelected ? { weight: "bold" } : undefined}
                        >
                            {node.shortDescription}
                            {displayCount > 0 ? ` (${displayCount})` : ""}
                        </Checkbox>
                    </CheckboxGroup>
                    {children.length > 0 && (
                        <CategoryCheckboxTree
                            nodes={children}
                            selectedCategoryIds={selectedCategoryIds}
                            depth={depth + 1}
                            onToggle={onToggle}
                        />
                    )}
                </React.Fragment>
            );
        })}
    </>
);

const accordionSectionStyles = {
    headerProps: {
        buttonProps: {
            background: getColor("primary.main"),
            color: getContrastColor("primary.main"),
            
        },
        css: css`
            button {
                background: ${getColor("primary.main")};
                color: ${getContrastColor("primary.main")};
            }
        `,
    },
    titleTypographyProps: {
        weight: 700,
    },
    panelProps: {
        css: css`
            overflow-y: auto;
            overflow-x: hidden;
            max-height: 350px;
            padding-top: 10px;
            padding-bottom: 10px;
        `,
    },
};

const ClearanceCategoryFilters: FC<Props> = ({
    appliedProductIds,
    isCategoryPage,
    applyClearanceProductIds,
    fields,
    category,
}) => {
    const [tree, setTree] = useState<ClearanceCategoryNode[]>([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set());
    // Tracks the applied-product-id "key" the local selection is already in sync with, so we can tell our own
    // (optimistic/debounced) applies apart from external changes coming from the Remove Clearance Filters widget.
    const syncedKeyRef = useRef<string | null>(null);

    const clearanceCategoryId = category?.id;
    const isClearance = isClearanceCategory(category);

    useEffect(() => {
        let isMounted = true;

        if (isClearance && clearanceCategoryId) {
            getClearanceCategoryTree({ categoryId: clearanceCategoryId })
                .then(response => {
                    if (isMounted && response?.success) {
                        setTree(response.categories || []);
                    }
                })
                .catch(error => logger.error(error));
        } else {
            setTree([]);
        }

        return () => {
            isMounted = false;
        };
    }, [clearanceCategoryId, isClearance]);

    const maps = useMemo(() => buildMaps(tree), [tree]);

    // The applied product ids can change from outside this widget - the Remove Clearance Filters widget removes a
    // tag or clears all. Re-derive the checkbox selection from them whenever they change for a reason other than
    // our own apply. Our optimistic toggles below record their expected key in syncedKeyRef, so the echo of our
    // own (debounced) dispatch is skipped and the instant local state is preserved.
    const appliedKey = useMemo(
        () => (appliedProductIds ? [...appliedProductIds].sort().join(",") : ""),
        [appliedProductIds],
    );

    useEffect(() => {
        if (!tree.length || syncedKeyRef.current === appliedKey) {
            return;
        }
        syncedKeyRef.current = appliedKey;
        setSelectedCategoryIds(deriveSelectedCategoryIds(maps, appliedProductIds));
    }, [tree, appliedKey, appliedProductIds, maps]);

    // Selecting several categories in quick succession would otherwise fire a full product reload (URL push
    // + filtered/unfiltered/pricing requests) per click. Debounce the dispatch so a burst of toggles collapses
    // into a single reload while the checkboxes still update instantly from local state.
    const debouncedApply = useMemo(
        () => debounce((productIds: string[]) => applyClearanceProductIds(productIds, clearanceCategoryId), 300),
        [applyClearanceProductIds, clearanceCategoryId],
    );
    useEffect(() => () => debouncedApply.cancel(), [debouncedApply]);

    if ((isCategoryPage && !isClearance) || !tree.length) {
        return null;
    }

    const onToggle = (node: ClearanceCategoryNode) => {
        const next = new Set(selectedCategoryIds);
        const branch = collectSelfAndDescendants(node);

        if (next.has(node.id)) {
            branch.forEach(id => next.delete(id));
            let parentId = maps.parentById.get(node.id);
            while (parentId) {
                next.delete(parentId);
                parentId = maps.parentById.get(parentId);
            }
        } else {
            branch.forEach(id => next.add(id));
            let parentId = maps.parentById.get(node.id);
            while (parentId) {
                const parentNode = maps.nodeById.get(parentId);
                const allChildrenSelected = (parentNode?.subCategories ?? []).every(child => next.has(child.id));
                if (!allChildrenSelected) {
                    break;
                }
                next.add(parentId);
                parentId = maps.parentById.get(parentId);
            }
        }

        const productIds = computeProductIds(next, maps);
        // Record the key we're about to apply so the eventual redux echo of this dispatch doesn't re-derive and
        // clobber the optimistic selection we just set.
        syncedKeyRef.current = [...productIds].sort().join(",");
        setSelectedCategoryIds(next);
        debouncedApply(productIds);
    };

    return (
        <StyledWrapper
            css={css`
                margin-bottom: 20px;
            `}
        >
            {tree.map(topNode => {
                const childNodes = topNode.subCategories?.length ? topNode.subCategories : [topNode];
                return (
                    <ManagedAccordionSection
                        key={topNode.id}
                        title={topNode.name}
                        initialExpanded={fields.expandByDefault || branchHasSelection(topNode, selectedCategoryIds)}
                        {...accordionSectionStyles}
                    >
                        <CategoryCheckboxTree
                            nodes={childNodes}
                            selectedCategoryIds={selectedCategoryIds}
                            depth={0}
                            onToggle={onToggle}
                        />
                    </ManagedAccordionSection>
                );
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCategory(ClearanceCategoryFilters)),
    definition: {
        group: "Product List",
        displayName: "Clearance Category Filters",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.expandByDefault,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
